// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { PRIVACY_URL } from './layout.js';
import {
  PLATFORM_TERMS_EFFECTIVE_DATE,
  PLATFORM_TERMS_RISKS,
  PLATFORM_TERMS_VERSION,
} from '../pages/platform-terms.js';

/**
 * COB-512 (E1) — the Platform Terms + risk disclosure shown on the two
 * surfaces that put a signing key on a shared vault.
 *
 * **What must stay visible without a click** (COB-603 revised COB-512's
 * everything-in-place layout, 2026-08-27): the sentence naming the document,
 * its version and the new-tab link to it; the fold summaries, which name what
 * they contain; and the acceptance sentence directly above the button.
 * Pressing the button is the acceptance, and the server records the version
 * that was on the page.
 *
 * **What sits behind a fold, and why.** The clause 8.1 risk list and the
 * joiner's visibility notice are long, and laid out in place they pushed the
 * consent button below the fold of the page — a wall of text that reads as
 * scroll-past material. COB-603's call: native `<details>`, collapsed by
 * default, so the reader expands in place or accepts without the scroll. The
 * full text stays in the DOM either way. Still no modal and no second
 * checkbox — the claim-link page's design note that one "would be dismissed
 * unread" stands.
 *
 * **Why the risks render from {@link PLATFORM_TERMS_RISKS}.** They are clause
 * 8.1 itself, not a summary of it — a paraphrase here would be a second text
 * that could drift from the document the user is accepting.
 *
 * **Why the Terms link opens a new tab.** Both host pages hold state that a
 * navigation destroys: the claim page keeps the single-use invite token in an
 * in-memory ref and the session JWT is in-memory only (no restore on reload),
 * and the create wizard holds an unsubmitted roster. Reading the full Terms
 * must never cost the user their place.
 */
export type PlatformTermsSurface = 'accept' | 'create';

/** The button each surface's disclosure points at, quoted verbatim so the
 *  sentence names the control the user is about to press. */
const CTA_LABEL: Record<PlatformTermsSurface, string> = {
  accept: 'Consent to participate',
  create: 'Create shared vault',
};

/** COB-521 (B5) Do-1 — the direct-collection disclosures a joiner is owed
 *  *before* they provide an email or a key. Since COB-603 the body sits one
 *  tap behind a fold whose summary carries the title; the notice is still
 *  delivered on the surface where the decision is taken, before any key
 *  exists.
 *
 *  All three are lifted from privacy policy §5 ("If you were sent a claim link
 *  to join a vault"), which is the notice these restate: what co-participants
 *  see and that it is reciprocal, that the creator is emailed the joiner's
 *  account email the moment they claim, and that walking away leaves nothing
 *  held. §5 is the authority; this is the same statement placed where the
 *  decision is actually taken. Accept-path only — the creator's counterpart
 *  disclosures are in §5's closing paragraph and on the claim-link screen. */
function ParticipantVisibilityNotice() {
  return (
    <div class="sv-terms-visibility" data-testid="sv-participant-visibility">
      <ul>
        <li>
          <strong>The other participants will see</strong> the email address on
          your Cobault account, the public key your browser generates for this
          vault, and the fact and timing of your acknowledgements and
          signatures. You will see theirs. A shared vault cannot be joined
          anonymously as against your co-participants.
        </li>
        <li>
          <strong>The person who created the vault is emailed your account
          email address</strong> at the moment you claim this slot, so they can
          check that the link reached the person they meant it for.
        </li>
        <li>
          <strong>If you decide not to join, close this page.</strong> Nothing
          about you is recorded, there is nothing for you to delete, and the
          link simply goes unused until it expires.
        </li>
      </ul>
      <p class="sv-terms-fineprint">
        Our{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
          privacy policy
        </a>{' '}
        (section 5) sets out the rest — why we collect this, how long we keep
        it, and your rights.
      </p>
    </div>
  );
}

export function PlatformTermsDisclosure({
  surface,
}: {
  surface: PlatformTermsSurface;
}) {
  const verb = surface === 'accept' ? 'join' : 'create';
  return (
    <section class="sv-terms-disclosure" data-testid="sv-platform-terms">
      <h3>Before you {verb} this vault</h3>
      <p>
        Shared vaults are governed by the{' '}
        <a href="/platform-terms" target="_blank" rel="noopener noreferrer">
          Cobault Platform Terms
        </a>{' '}
        (version {PLATFORM_TERMS_VERSION}, effective{' '}
        {PLATFORM_TERMS_EFFECTIVE_DATE} — opens in a new tab, so you keep your
        place here). Clause 8 sets out the risks you accept by {verb}ing one —
        reproduced in full below. Every signing key belongs to a participant:
        Cobault holds none, and cannot recover or replace yours.
      </p>
      <details class="sv-terms-fold" data-testid="sv-terms-risks-fold">
        <summary>The risks you accept — Platform Terms, clause 8</summary>
        <ul class="sv-terms-risks">
          {PLATFORM_TERMS_RISKS.map((risk) => (
            <li key={risk.title}>
              <strong>{risk.title}</strong> {risk.body}
            </li>
          ))}
        </ul>
      </details>
      {surface === 'accept' && (
        <details class="sv-terms-fold" data-testid="sv-visibility-fold">
          <summary>What joining reveals about you</summary>
          <ParticipantVisibilityNotice />
        </details>
      )}
      <p
        class="sv-terms-accept-line"
        data-testid="sv-platform-terms-accept-line"
      >
        Pressing “{CTA_LABEL[surface]}” accepts the Cobault Platform Terms,
        version {PLATFORM_TERMS_VERSION} — including the clause 8 risks above —
        and records that acceptance against this vault.
      </p>
    </section>
  );
}
